// src/StaffingServices.js (New Component - Create this file in src/)
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const Staffing = ({ onBack }) => {
   useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  return (
    <div className="bg-white min-h-screen text-black font-sans">
      {/* Navigation with Back Button */}
      <nav className="sticky top-0 z-50 bg-white shadow-md px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center">
          <motion.button
            onClick={onBack}
            className="flex items-center text-red-500 hover:text-red-600 text-lg font-semibold transition-colors"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            ← Back to Home
          </motion.button>
          <h1 className="text-2xl font-bold">
            <span className="text-red-500">Brain</span>Serve
          </h1>
        </div>
      </nav>

      {/* Hero Section for Staffing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Staffing 
            <span className="text-red-500 block">Services</span>
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Building high-performing teams with precision staffing. Brain Serve connects top talent to businesses, 
            offering flexible solutions for temporary, contract, and permanent roles across industries.
          </motion.p>
          <button className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-red-500/50">
            Find Talent
          </button>
        </div>
      </section>

      {/* Staffing Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.h2 
            className="text-4xl font-bold text-center mb-16 text-black"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Our Staffing Solutions
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Permanent Recruitment */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🔗</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Permanent Recruitment</h3>
              <p className="text-gray-600 mb-4 text-center">Full-time hires for long-term growth, with rigorous screening and 6-month guarantee.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• End-to-end hiring process</li>
                <li>• Industry-specific expertise</li>
                <li>• Onboarding support</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Post a Job
              </button>
            </motion.div>

            {/* Temporary Staffing */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">⏰</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Temporary Staffing</h3>
              <p className="text-gray-600 mb-4 text-center">Short-term talent for projects or seasonal needs, with quick turnaround and compliance ensured.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Flexible durations from days to months</li>
                <li>• Payroll and benefits management</li>
                <li>• Scalable workforce solutions</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Post a Job
              </button>
            </motion.div>

            {/* Contract Staffing */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">📋</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Contract Staffing</h3>
              <p className="text-gray-600 mb-4 text-center">Specialized contractors for niche skills, including IT, engineering, and executive roles.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Vendor-neutral sourcing</li>
                <li>• Performance tracking</li>
                <li>• Contract extensions handled</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Post a Job
              </button>
            </motion.div>

            {/* HR Consulting */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🛡️</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">HR Consulting</h3>
              <p className="text-gray-600 mb-4 text-center">Strategic HR advice on talent management, compliance, and organizational development.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Policy formulation and audits</li>
                <li>• Training and development programs</li>
                <li>• Diversity and inclusion strategies</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Post a Job
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Staffing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <motion.h2 
            className="text-4xl font-bold mb-8 text-black"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Why Brain Serve for Staffing?
          </motion.h2>
          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Vast Talent Pool</h3>
              <p className="text-gray-600">Access to 50,000+ pre-vetted professionals across sectors for rapid placements.</p>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Cost-Effective</h3>
              <p className="text-gray-600">Competitive pricing with no upfront fees and performance-based models.</p>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Proven Track Record</h3>
              <p className="text-gray-600">98% client retention and 4.8/5 satisfaction ratings from 500+ placements.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 text-center text-gray-500 border-t border-gray-200">
        <p>&copy; 2025 Brain Serve. All rights reserved. | Talent That Transforms.</p>
      </footer>
    </div>
  );
};

export default Staffing;